import { starsForRating } from '../config';

interface StarRatingProps {
  rating: number;
  reviewCount?: number;
  /** Show the numeric score before the stars (the design does on cards + detail). */
  showScore?: boolean;
  className?: string;
}

/**
 * Star rating with optional score and review count (BCP-2057 §3).
 *
 * Renders NOTHING for an unrated product — not zero stars, not "0 reviews".
 * Which stars show comes from `starsForRating` (pure, unit-tested). The stars
 * themselves are the Shows card's Font Awesome icons (`NearbyShowCard`):
 * `fas fa-star` full, `fas fa-star-half-alt` half, `fal fa-star` empty, so a
 * rating looks the same in both modules.
 *
 * Server-safe (no hooks) so it renders inside SSR pages directly.
 */
export function StarRating({ rating, reviewCount, showScore = true, className }: StarRatingProps) {
  const stars = starsForRating(rating);
  if (!stars) return null;

  const label =
    reviewCount != null
      ? `Rated ${rating} out of 5 from ${reviewCount} reviews`
      : `Rated ${rating} out of 5`;

  return (
    <span className={`atr-rating${className ? ` ${className}` : ''}`}>
      {showScore && <span className="atr-rating__score">{rating.toFixed(1)}</span>}
      <span className="atr-stars" role="img" aria-label={label}>
        {Array.from({ length: 5 }, (_, i) => {
          if (i < stars.full) return <i key={i} className="fas fa-star atr-star" aria-hidden="true" />;
          if (i === stars.full && stars.half)
            return <i key={i} className="fas fa-star-half-alt atr-star" aria-hidden="true" />;
          return <i key={i} className="fal fa-star atr-star" aria-hidden="true" />;
        })}
      </span>
      {reviewCount != null && reviewCount > 0 && (
        <span className="atr-rating__count">({reviewCount.toLocaleString('en-GB')} reviews)</span>
      )}
    </span>
  );
}
